import { Divider, Flex, Stack, Text } from '@chakra-ui/core'
import { Title } from 'components/TasksV2/UI/Title'
import { WidgetWrapper } from 'components/TasksV2/UI/WidgetWrapper'
import { RootState, Subtask } from 'constants/interfaces'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTaskOverviewContext } from '../../TaskOverviewStackProvider'
import useLocaleText from 'components/useLocaleText'
import { QuestionResultsList } from './QuestionResultsList'
import dayjs from 'dayjs'
import ExportToExcelButton from 'components/CommonComponents/ExportToExcelButton'
import { exportAnswersToExcel } from './utils'
import { findLatestSubtaskResults } from '../../model/results'
import { useGetTaskAudience } from 'hooks/useGetTaskAudience'

interface IProps {
  questionSubtask: Subtask
}
const QuestionsOverview: React.FC<IProps> = ({ questionSubtask }) => {
  const { tid, st_id, title } = questionSubtask
  const { task } = useTaskOverviewContext()
  const { tags, groups, users, is_individual_results, recurrence_ts, view } = task

  const { allResults } = useSelector((state: RootState) => state.tasks_v2)
  const {
    config: { groups: groupsObject, tags: retailTags, retailUsersObject },
    userWriteTaskPermissions: { users: permittedUsers, tags: permittedTagsIds },
  } = useSelector((state: RootState) => state.config)
  const getTaskAudience = useGetTaskAudience()

  const permittedTags = useMemo(
    () => retailTags?.filter((tag) => permittedTagsIds?.includes(tag.sk)),
    [permittedTagsIds, retailTags]
  )

  const { groups: groupsAudience, users: usersAudience } = useMemo(
    () =>
      getTaskAudience({
        retailTags: permittedTags,
        groups,
        users,
        tags,
        is_individual_results,
        view,
      }),
    [getTaskAudience, groups, is_individual_results, permittedTags, tags, users, view]
  )

  const t_do_not_voted = useLocaleText('t_do_not_voted')

  const audience = [...groupsAudience, ...usersAudience.filter((participant) => permittedUsers.includes(participant))]

  const contextsWithResults = useMemo(
    () => findLatestSubtaskResults(allResults?.[tid], st_id, recurrence_ts),
    [allResults, recurrence_ts, st_id, tid]
  )

  const contextsWithoutResults = audience.filter(
    (participant) => !contextsWithResults.find((result) => result.context_id === participant)
  )

  const handleExportToExcel = () => {
    exportAnswersToExcel(contextsWithResults, contextsWithoutResults, groupsObject, retailUsersObject!, title)
  }

  return (
    <>
      {contextsWithResults && (
        <Flex mx="auto" mt="10px" mb="20px" justifyContent="center">
          <ExportToExcelButton actionWithoutArgs={handleExportToExcel} />
        </Flex>
      )}
      <Stack direction="column" spacing={4}>
        <WidgetWrapper p="15px">
          {contextsWithResults.length > 0 && (
            <QuestionResultsList
              contextsList={contextsWithResults.map(({ context_id, created_at_ts, answer }) => ({
                contextId: context_id,
                date: dayjs(created_at_ts! * 1000).format('YYYYMMDDHHmmss'),
                ...(answer !== null ? { answer } : {}),
              }))}
            />
          )}
        </WidgetWrapper>

        {contextsWithoutResults.length > 0 && (
          <WidgetWrapper p="20px">
            <Flex flexDir="row" justifyContent="space-between" fontWeight="500" fontSize="18px" fontFamily="Arial">
              <Title
                title={t_do_not_voted}
                w="max-context"
                isTruncated
                fontWeight="500"
                fontFamily="Arial"
                fontSize="18px"
              />
              <Text fontWeight="500" fontSize="18px" fontFamily="Arial">
                {contextsWithoutResults.length}
              </Text>
            </Flex>

            {contextsWithoutResults.length > 0 && (
              <>
                <Divider />
                <QuestionResultsList
                  contextsList={contextsWithoutResults.map((context) => ({
                    contextId: context,
                  }))}
                />
              </>
            )}
          </WidgetWrapper>
        )}
      </Stack>
    </>
  )
}

export default QuestionsOverview
